// src/components/CustomerPayBill.js
import React, { useState, useEffect } from "react";
import api from "../Service/api";
import "./PayBill.css";

const CustomerPayBill = () => {
  const [customerList, setCustomerList] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [description, setDescription] = useState("");
  const [customerInvoiceNumber, setCustomerInvoiceNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [customerBalance, setCustomerBalance] = useState(null);
  const [formKey, setFormKey] = useState(Date.now());

  // Fetch next invoice number on component mount
  useEffect(() => {
    fetchNextInvoiceNumber();
  }, []);

  const fetchNextInvoiceNumber = async () => {
    try {
      console.log("Fetching next customer invoice number...");
      const res = await api.get("/billPayment/customer/next-invoice");
      
      console.log("API Response:", res.data);
      
      if (res.data && res.data.nextInvoice !== undefined) {
        const nextNumber = res.data.nextInvoice;
        const newInvoiceNumber = `C-${nextNumber}`;
        console.log("Setting to:", newInvoiceNumber);
        setCustomerInvoiceNumber(newInvoiceNumber);
        return newInvoiceNumber;
      } else {
        console.log("Invalid response, using C-1");
        setCustomerInvoiceNumber('C-1');
        return 'C-1';
      }
    } catch (err) {
      console.error("Error fetching next invoice:", err);
      setCustomerInvoiceNumber('C-1');
      return 'C-1';
    }
  };

  useEffect(() => {
    if (!customerName) {
      setCustomerBalance(null);
      return;
    }

    const fetchCustomerBalance = async () => {
      try {
        const res = await api.get(
          `/billPayment/customer-balance/${customerName}`
        );

        if (res.data.code === 1) {
          setCustomerBalance(res.data.additionalDetail);
        } else {
          setCustomerBalance(null);
        }
      } catch (err) {
        console.error("Error fetching balance:", err);
        setCustomerBalance(null);
      }
    };

    fetchCustomerBalance();
  }, [customerName]);

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api.get("/product/getAllCustomers");
        if (res.data.code === 1 && res.data.additionalDetail) {
          setCustomerList(res.data.additionalDetail);
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
  const handleWheel = (e) => {
    if (e.target.type === 'number') {
      e.preventDefault();
    }
  };
  document.addEventListener('wheel', handleWheel, { passive: false });
  return () => document.removeEventListener('wheel', handleWheel);
}, []);

  const resetForm = async () => {
    // Reset loading state first
    setLoading(false);
    
    // Fetch next invoice number
    const newInvoiceNumber = await fetchNextInvoiceNumber();
    
    // Reset all fields
    setCustomerName("");
    setAmountPaid("");
    setDescription("");
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setCustomerBalance(null); // Reset balance display
    
    // Force re-render by updating key
    setFormKey(Date.now());
    
    console.log("Form reset complete. New invoice:", newInvoiceNumber);
  };

  const handleSubmit = async () => {
    if (!customerName || !amountPaid) {
      alert("Please select a customer and enter the amount.");
      return;
    }

    if (!customerInvoiceNumber) {
      alert("Invoice number is being generated. Please wait...");
      return;
    }

    setLoading(true);

    try {
      console.log("Sending payload with invoice:", customerInvoiceNumber);

      const res = await api.post("/billPayment/customer_pay_bill", {
        customerName,
        amountPaid: parseFloat(amountPaid),
        description: description,
        paymentDate: paymentDate,
        customerInvoiceNumber: customerInvoiceNumber
      });

      console.log("Payment response:", res.data);

      if (res.data.code === 1 || res.data.success) {
        alert(
          `Payment Successful! Invoice: ${customerInvoiceNumber}\n` +
          (res.data.additionalDetail || `Remaining Balance: ${res.data.remainingBalance}`)
        );
        
        // Reset form and fetch new invoice number
        await resetForm();
        
      } else {
        alert(res.data.additionalDetail || res.data.message || "Payment failed");
        setLoading(false); // Make sure to reset loading on failure
      }
    } catch (err) {
      console.error("Payment error:", err);
      const msg =
        err.response?.data?.additionalDetail ||
        err.response?.data?.message ||
        "Something went wrong";
      alert(msg);
      setLoading(false); // Reset loading on error
    }
  };

  return (
    <div className="category-form-container" key={formKey}>
      <h2>💳 Receive Customer Bill</h2>

      <div className="category-table">
        {/* Customer Name */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Customer Name</label>
          <input
            type="text"
            list="customerNames"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Enter Customer Name"
            className="category-input"
          />
          {customerBalance !== null && (
            <table className="category-table" style={{ marginBottom: "15px", marginTop: "10px" }}>
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Outstanding Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{customerName}</td>
                  <td
                    style={{
                      fontWeight: "bold",
                      color: customerBalance < 0 ? "#dc2626" : "#16a34a"
                    }}
                  >
                    {customerBalance}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          <datalist id="customerNames">
            {customerList.map((c, i) => (
              <option key={i} value={c.customerName} />
            ))}
          </datalist>
        </div>

        {/* Invoice Number Display */}
        {customerInvoiceNumber && (
          <div style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: "bold" }}>Payment Number</label>
            <input
              type="text"
              value={customerInvoiceNumber}
              readOnly
              className="category-input"
              style={{ 
                backgroundColor: "#f0f0f0",
                color: "#16a34a",
                fontWeight: "bold",
                border: "1px solid #16a34a"
              }}
            />
          </div>
        )}

        {/* Amount Paid */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Receive Bill</label>
          <input
            type="number"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            placeholder="Enter amount"
            className="category-input"
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            className="category-input"
          />
        </div>

        {/* Payment Date */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Payment Date</label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="category-input"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Submit Button */}
        <button
          className="save-btn"
          onClick={handleSubmit}
          disabled={loading || !customerInvoiceNumber}
        >
          {loading ? "Processing..." : "Receive Bill"}
        </button>
      </div>
    </div>
  );
};

export default CustomerPayBill;